import type { ReactNode } from "react";

function toneStyles(tone: "ready" | "review" | "blocked") {
  if (tone === "ready") {
    return {
      border: "1px solid rgba(44, 93, 67, 0.14)",
      background:
        "linear-gradient(180deg, rgba(244, 250, 246, 0.96), rgba(255, 252, 248, 0.98))",
      eyebrowColor: "#214735",
    };
  }

  if (tone === "blocked") {
    return {
      border: "1px solid rgba(162, 62, 62, 0.16)",
      background:
        "linear-gradient(180deg, rgba(255, 245, 243, 0.96), rgba(255, 252, 248, 0.98))",
      eyebrowColor: "#812d2d",
    };
  }

  return {
    border: "1px solid rgba(181, 141, 34, 0.16)",
    background:
      "linear-gradient(180deg, rgba(255, 250, 239, 0.96), rgba(255, 252, 248, 0.98))",
    eyebrowColor: "#6d5210",
  };
}

function ValueCard(props: {
  eyebrow: string;
  title: string;
  tone: "ready" | "review" | "blocked";
  children: ReactNode;
}) {
  const tone = toneStyles(props.tone);

  return (
    <section
      style={{
        borderRadius: 24,
        border: tone.border,
        background: tone.background,
        padding: "1rem",
        boxShadow: "0 18px 34px rgba(77, 61, 44, 0.06)",
      }}
    >
      <div
        style={{
          fontSize: "0.72rem",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: tone.eyebrowColor,
          fontWeight: 700,
        }}
      >
        {props.eyebrow}
      </div>
      <h2
        style={{
          margin: "0.45rem 0 0",
          fontSize: "1.06rem",
          lineHeight: 1.2,
          color: "#26221d",
          fontWeight: 700,
        }}
      >
        {props.title}
      </h2>
      <div
        style={{
          marginTop: "0.8rem",
          color: "#4d453d",
          lineHeight: 1.7,
          fontSize: "0.95rem",
        }}
      >
        {props.children}
      </div>
    </section>
  );
}

export default function StudioBusinessValueBand(props: {
  title: string;
  summary: string;
  visibleNow: string[];
  gatedNow: string[];
  nextMove: string[];
}) {
  return (
    <section
      aria-label="Business-value narrative"
      style={{
        marginTop: "1rem",
        display: "grid",
        gap: "0.9rem",
      }}
    >
      <div
        style={{
          borderRadius: 26,
          border: "1px solid rgba(41, 84, 103, 0.14)",
          background:
            "linear-gradient(180deg, rgba(240, 248, 250, 0.92), rgba(248, 251, 252, 0.98))",
          padding: "1rem 1.05rem",
        }}
      >
        <div
          style={{
            fontSize: "0.74rem",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "#5b7884",
            fontWeight: 700,
          }}
        >
          Founder-demo posture
        </div>
        <h2
          style={{
            margin: "0.42rem 0 0",
            fontSize: "1.16rem",
            color: "#26221d",
            fontWeight: 700,
          }}
        >
          {props.title}
        </h2>
        <p style={{ margin: "0.65rem 0 0", color: "#39535f", lineHeight: 1.7 }}>
          {props.summary}
        </p>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: "0.9rem",
        }}
      >
        <ValueCard
          eyebrow="Visible now"
          title="What Studio already helps you do"
          tone="ready"
        >
          <ul style={{ margin: 0, paddingLeft: "1.1rem" }}>
            {props.visibleNow.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </ValueCard>

        <ValueCard
          eyebrow="Still gated"
          title="What Studio intentionally does not do yet"
          tone="blocked"
        >
          <ul style={{ margin: 0, paddingLeft: "1.1rem" }}>
            {props.gatedNow.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </ValueCard>

        <ValueCard
          eyebrow="Next move"
          title="What should happen next"
          tone="review"
        >
          <ul style={{ margin: 0, paddingLeft: "1.1rem" }}>
            {props.nextMove.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </ValueCard>
      </div>
    </section>
  );
}
